// Media-attachment state helpers shared by PostCreator, CommandCenterPostCreator,
// and MediaGalleryPage. All three consumers keep their gallery/form state in
// sync here; upload transport is centralized in useMediaUploadQueue.
import { captionFileName, serializeCaptionTracks } from '../../utils/captionTracks'

export { normalizeCaptionTracks, serializeCaptionTracks } from '../../utils/captionTracks'

export const MEDIA_UPLOAD_ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/avif',
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/webm',
  '.mov',
  '.m4v',
].join(',')

const MEDIA_KIND_BY_EXTENSION = {
  avif: 'image',
  gif: 'image',
  jpeg: 'image',
  jpg: 'image',
  png: 'image',
  webp: 'image',
  m4v: 'video',
  mov: 'video',
  mp4: 'video',
  webm: 'video',
}

const MEDIA_KIND_BY_CONTENT_TYPE = {
  'image/avif': 'image',
  'image/gif': 'image',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/webp': 'image',
  'video/mp4': 'video',
  'video/quicktime': 'video',
  'video/webm': 'video',
  'video/x-m4v': 'video',
}

export function mediaUploadKind(file) {
  const contentType = String(file?.type || '').toLocaleLowerCase()
  const extension = String(file?.name || '').split('.').pop()?.toLocaleLowerCase()
  return MEDIA_KIND_BY_CONTENT_TYPE[contentType] || MEDIA_KIND_BY_EXTENSION[extension] || null
}

export function mediaUploadContentType(file) {
  const contentType = String(file?.type || '').toLocaleLowerCase()
  if (MEDIA_KIND_BY_CONTENT_TYPE[contentType]) return contentType
  const extension = String(file?.name || '').split('.').pop()?.toLocaleLowerCase()
  if (extension === 'mov') return 'video/quicktime'
  if (extension === 'm4v' || extension === 'mp4') return 'video/mp4'
  if (extension === 'webm') return 'video/webm'
  return contentType
}

export function validateMediaUploadFile(file) {
  const kind = mediaUploadKind(file)
  if (!kind) {
    return 'Use PNG, JPEG, GIF, WebP, AVIF, MP4, MOV, M4V, or WebM.'
  }
  const maxBytes = kind === 'video' ? 200 * 1024 * 1024 : 25 * 1024 * 1024
  if (file.size > maxBytes) {
    return kind === 'video'
      ? 'Video files must be 200 MB or smaller.'
      : 'Image files must be 25 MB or smaller.'
  }
  return ''
}

// Caption tracks are sidecar WebVTT files of a video. In the composer they
// live as `captionTracks` on the `uploadedMedia` entry (UI state, with ids and
// file names) and as `captions` on the matching `formData.mediaObjects` entry
// (what gets submitted). A media object without a `captions` key keeps the
// tracks the server already stores for it; an empty list clears them.
export const CAPTION_UPLOAD_ACCEPT = 'text/vtt,.vtt'
export const MAX_CAPTION_TRACKS = 10
export const MAX_CAPTION_UPLOAD_BYTES = 2 * 1024 * 1024
export const CAPTION_KIND_OPTIONS = [
  { value: 'subtitles', label: 'Subtitles' },
  { value: 'captions', label: 'Captions (includes sound cues)' },
]

const CAPTION_LANG_PATTERN = /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/
const CAPTION_LANG_MAX_LENGTH = 35
const CAPTION_LABEL_MAX_LENGTH = 80

export function validateCaptionUploadFile(file) {
  const name = String(file?.name || '').toLocaleLowerCase()
  const contentType = String(file?.type || '').toLocaleLowerCase().split(';')[0].trim()
  if (!name.endsWith('.vtt') && contentType !== 'text/vtt') {
    return 'Use a WebVTT (.vtt) caption file.'
  }
  if (file.size > MAX_CAPTION_UPLOAD_BYTES) {
    return 'Caption files must be 2 MB or smaller.'
  }
  return ''
}

export function isValidCaptionLang(lang) {
  const value = String(lang || '').trim().toLocaleLowerCase()
  return value.length <= CAPTION_LANG_MAX_LENGTH && CAPTION_LANG_PATTERN.test(value)
}

// "clip.en.vtt" -> "en", "clip.pt-BR.vtt" -> "pt-br"; anything else -> "".
export function captionLangFromFilename(name) {
  const parts = String(name || '').split('.')
  if (parts.length < 3 || parts.pop().toLocaleLowerCase() !== 'vtt') return ''
  const candidate = parts.pop().toLocaleLowerCase()
  return isValidCaptionLang(candidate) ? candidate : ''
}

export function captionLabelForLang(lang) {
  const value = String(lang || '').trim()
  if (!value) return ''
  try {
    const label = new Intl.DisplayNames(['en'], { type: 'language' }).of(value)
    if (label && label.toLocaleLowerCase() !== value.toLocaleLowerCase()) return label
  } catch {
    // Intl.DisplayNames is missing or rejects the code: fall back below.
  }
  return value.toUpperCase()
}

export function createCaptionTrack({ url, objectKey = null, fileName = '', lang = '', label = '', kind = 'subtitles' } = {}) {
  return {
    id: crypto.randomUUID(),
    url: url || '',
    objectKey,
    fileName: fileName || captionFileName(url),
    lang: String(lang || ''),
    label: String(label || ''),
    kind: kind === 'captions' ? 'captions' : 'subtitles',
  }
}

export function captionTrackIssues(tracks = []) {
  const issues = []
  const seen = new Set()
  for (const track of tracks || []) {
    const lang = String(track.lang || '').trim().toLocaleLowerCase()
    const kind = track.kind === 'captions' ? 'captions' : 'subtitles'
    if (!lang) {
      issues.push({ trackId: track.id, message: 'language is required' })
    } else if (!isValidCaptionLang(lang)) {
      issues.push({ trackId: track.id, message: 'use a language code such as en or pt-br' })
    } else if (seen.has(`${lang}:${kind}`)) {
      issues.push({ trackId: track.id, message: 'another track already uses this language and kind' })
    } else if (String(track.label || '').trim().length > CAPTION_LABEL_MAX_LENGTH) {
      issues.push({ trackId: track.id, message: `label must be ${CAPTION_LABEL_MAX_LENGTH} characters or fewer` })
    }
    if (lang) seen.add(`${lang}:${kind}`)
  }
  return issues
}

export function describeCaptionTrackIssues(uploadedMedia = []) {
  for (const media of uploadedMedia || []) {
    const issues = captionTrackIssues(media.captionTracks)
    if (issues.length > 0) {
      return `Fix the caption tracks on ${media.name || 'this video'}: ${issues[0].message}.`
    }
  }
  return ''
}

function mediaObjectKey(media) {
  return media?.mediaObjectId || media?.id
}

// Writes a video's tracks to both pieces of composer state. `tracksOrUpdater`
// is either the next track list or a function `(currentTracks) => nextTracks`
// resolved inside each functional setter, so concurrent edits (an upload
// finishing while the operator edits another track) are never lost. The
// updater sees gallery tracks in one setter and the submitted `captions` in
// the other; both carry url/lang/label/kind, which is all serialization reads.
export function setMediaCaptionTracks(mediaObjectId, tracksOrUpdater, { setUploadedMedia, setFormData }) {
  const resolve = (currentTracks) => (
    typeof tracksOrUpdater === 'function'
      ? tracksOrUpdater(currentTracks || [])
      : tracksOrUpdater
  ) || []
  setUploadedMedia((current) => current.map((media) => (
    mediaObjectKey(media) === mediaObjectId ? { ...media, captionTracks: resolve(media.captionTracks) } : media
  )))
  setFormData((current) => ({
    ...current,
    mediaObjects: current.mediaObjects.map((media) => (
      mediaObjectKey(media) === mediaObjectId
        ? { ...media, captions: serializeCaptionTracks(resolve(media.captions)) }
        : media
    )),
  }))
}

// Owner-organization metadata is *operation context*, never post identity: it
// records which organization's media library physically holds an asset so
// upload/delete/transcription calls hit the right org-scoped endpoint. It rides
// on `uploadedMedia` (UI state) only and is deliberately absent from
// `mediaObject`, which is what gets serialized into the post document.
// `normalizeMediaOwner` takes an *organization* record ({ id, name, slug });
// `readMediaOwner` recovers owner fields already stamped onto a media or asset
// record. They are deliberately separate: an asset's own `id` is its media id,
// never an organization id, so a single lenient reader would misattribute.
export function normalizeMediaOwner(organization) {
  const organizationId = organization?.id || null
  if (!organizationId) return null
  return {
    organizationId,
    organizationName: organization.name || '',
    organizationSlug: organization.slug || '',
  }
}

export function readMediaOwner(record) {
  const organizationId = record?.organizationId || record?.organization_id || null
  if (!organizationId) return null
  return {
    organizationId,
    organizationName: record.organizationName || record.organization_name || '',
    organizationSlug: record.organizationSlug || record.organization_slug || '',
  }
}

export function mediaOwnerOrganizationId(media) {
  return media?.organizationId || null
}

// Tags every asset in one org's library response with that org so an aggregated
// multi-library list stays attributable after the responses are merged.
export function annotateLibraryAssetOwner(assets = [], organization) {
  const ownerFields = normalizeMediaOwner(organization)
  if (!ownerFields) return assets
  return assets.map((asset) => ({ ...asset, ...ownerFields }))
}

export function groupMediaAssetsByOwner(assets = [], organizations = [], limitPerOwner = 12) {
  return organizations
    .map((organization) => ({
      organizationId: organization.id,
      organizationName: organization.name,
      assets: assets
        .filter((asset) => (asset.organizationId || asset.organization_id) === organization.id)
        .slice(0, limitPerOwner),
    }))
    .filter((group) => group.assets.length > 0)
}

// Attachments whose owning organization is no longer a selected destination.
// They are never silently dropped — the composer blocks review and asks the
// operator to re-select the owner or remove the media.
export function findOrphanedMediaOwners(uploadedMedia = [], selectedOrganizationIds = []) {
  const selected = new Set(selectedOrganizationIds)
  const groups = new Map()
  for (const media of uploadedMedia) {
    const organizationId = mediaOwnerOrganizationId(media)
    if (!organizationId || selected.has(organizationId)) continue
    const group = groups.get(organizationId) || {
      organizationId,
      organizationName: media.organizationName || 'another organization',
      mediaNames: [],
    }
    group.mediaNames.push(media.name || 'media')
    groups.set(organizationId, group)
  }
  return [...groups.values()]
}

export function describeOrphanedMediaOwners(groups = []) {
  return groups
    .map((group) => (
      `Media owned by ${group.organizationName} is still attached: ${group.mediaNames.join(', ')}.`
      + ` Re-select ${group.organizationName} as a destination, or remove that media.`
    ))
    .join(' ')
}

export function createMediaAttachment(asset, fallbackName = 'media', owner = null) {
  const attachmentId = crypto.randomUUID()
  const assetId = asset.mediaId || asset.id || null
  const objectKey = asset.object_key || asset.objectKey || null
  const sourceObjectKey = asset.source_object_key || asset.sourceObjectKey || null
  const type = asset.asset_type || asset.type
  const url = asset.url || asset.previewUrl
  const name = asset.title || asset.name || fallbackName
  const ownerFields = normalizeMediaOwner(owner) || readMediaOwner(asset)

  return {
    uploadedMedia: {
      id: attachmentId,
      mediaObjectId: attachmentId,
      mediaId: assetId,
      objectKey,
      sourceObjectKey,
      type,
      url,
      previewUrl: url,
      name,
      uploading: false,
      ...(type === 'video' ? { captionTracks: [] } : {}),
      ...(ownerFields || {}),
      // A library asset's moderation state, shown on the attached card; it
      // never goes into the saved media objects.
      ...(asset.moderation ? { moderation: asset.moderation } : {}),
    },
    mediaObject: {
      id: attachmentId,
      mediaObjectId: attachmentId,
      mediaId: assetId,
      object_key: objectKey,
      source_object_key: sourceObjectKey,
      type,
      url,
      name,
    },
  }
}

export function createUploadPlaceholder(file, owner = null) {
  const previewUrl = URL.createObjectURL(file)
  const mediaType = mediaUploadKind(file)
  const placeholderId = crypto.randomUUID()
  const ownerFields = normalizeMediaOwner(owner)

  return {
    placeholderId,
    previewUrl,
    mediaType,
    owner: ownerFields,
    placeholder: {
      id: placeholderId,
      type: mediaType,
      name: file.name,
      previewUrl,
      url: null,
      uploading: true,
      uploadProgress: 0,
      ...(mediaType === 'video' ? { captionTracks: [] } : {}),
      ...(ownerFields || {}),
    },
  }
}

const VIDEO_ANALYSIS_ERROR_COPY = {
  invalid_input: 'This video cannot be analyzed. Replace it with a supported video.',
  unsupported_media: 'This video cannot be analyzed. Replace it with a supported video.',
  invalid_media: 'This video cannot be analyzed. Replace it with a supported video.',
  media_too_large: 'Video is over 25 MB. Replace it to use video analysis.',
  input_too_large: 'Video is over 25 MB. Replace it to use video analysis.',
  file_too_large: 'Video is over 25 MB. Replace it to use video analysis.',
  not_configured: 'Video analysis is unavailable for this organization.',
  disabled: 'Video analysis is unavailable for this organization.',
}

function absoluteStartedAt(value, fallback = Date.now()) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Date.parse(value || '')
  return Number.isNaN(parsed) ? fallback : parsed
}

function analysisFailure(error) {
  const code = error?.code || 'analysis_failed'
  if (code === 'media_too_large' || code === 'input_too_large' || code === 'file_too_large') {
    return { status: 'too_large', errorCode: code, error: VIDEO_ANALYSIS_ERROR_COPY[code], retryable: false }
  }
  if (code === 'not_configured' || code === 'disabled') {
    return { status: 'unavailable', errorCode: code, error: VIDEO_ANALYSIS_ERROR_COPY[code], retryable: false }
  }
  if (error?.retryable === true) {
    return {
      status: 'failed_retryable',
      errorCode: code,
      error: 'Video analysis could not finish. Try again.',
      retryable: true,
    }
  }
  return {
    status: 'invalid_input',
    errorCode: code,
    error: VIDEO_ANALYSIS_ERROR_COPY[code] || 'This video cannot be analyzed. Replace it with a supported video.',
    retryable: false,
  }
}

export function createUnavailableVideoAnalysis(reason = {}) {
  return {
    status: 'unavailable',
    errorCode: reason?.code || 'not_configured',
    error: 'Video analysis is unavailable for this organization.',
    retryable: false,
  }
}


export function createVideoAnalysisFromError(error) {
  return analysisFailure(error?.response?.data?.error || error?.error || error)
}

export function createVideoAnalysisFromJob(job, entry = {}) {
  const base = {
    ...entry,
    jobId: job?.id || entry.jobId,
    attempt: job?.attempt ?? entry.attempt ?? 1,
    retryOf: job?.retry_of ?? entry.retryOf ?? null,
    startedAt: absoluteStartedAt(entry.startedAt || job?.started_at || job?.created_at),
    completedAt: job?.completed_at || entry.completedAt || null,
  }

  if (job?.status === 'completed') {
    return {
      ...base,
      status: 'completed',
      error: '',
      errorCode: null,
      retryable: false,
    }
  }
  if (job?.status === 'failed') {
    return { ...base, ...analysisFailure(job.error) }
  }
  return {
    ...base,
    status: 'polling',
    error: '',
    errorCode: null,
    retryable: false,
  }
}

export function isRetryableVideoAnalysis(entry) {
  return entry?.status === 'failed_retryable' && entry.retryable === true && Boolean(entry.jobId)
}

export function attachAssetToState(asset, { setUploadedMedia, setFormData, owner = null }) {
  if (!asset?.url) return
  const attachment = createMediaAttachment(asset, 'media', owner)

  setUploadedMedia((current) => {
    if (current.some((item) => item.mediaId === asset.id || item.url === asset.url)) return current
    return [...current, attachment.uploadedMedia]
  })
  setFormData((current) => ({
    ...current,
    mediaObjects: current.mediaObjects.some((media) => media.mediaId === asset.id || media.url === asset.url)
      ? current.mediaObjects
      : [...current.mediaObjects, attachment.mediaObject],
  }))
}

export function removeMediaAtIndex(index, { uploadedMedia, setUploadedMedia, setFormData }) {
  const removedMedia = uploadedMedia[index]
  if (removedMedia?.previewUrl && String(removedMedia.previewUrl).startsWith('blob:')) {
    URL.revokeObjectURL(removedMedia.previewUrl)
  }
  setUploadedMedia((current) => current.filter((_, itemIndex) => itemIndex !== index))
  if (removedMedia?.url) {
    setFormData((current) => ({
      ...current,
      mediaObjects: current.mediaObjects.filter((media) => media.url !== removedMedia.url),
    }))
  }
}
